import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { ChampionshipCourt, ChampionshipDetail, ChampionshipMatch, ChampionshipMatchStatus, ChampionshipTeam, Referee } from "@/types";
import { MATCH_STATUS_LABEL, apiError, isoToLocalInput, localInputToIso, personName, teamLabel } from "@/lib/championships";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { SearchSelect } from "@/components/ui/SearchSelect";

const selectClass =
  "w-full rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";

// Pareja A/B, pista y árbitro se eligen con selectores con búsqueda en servidor: en un campeonato
// grande hay cientos de parejas y decenas de pistas, un desplegable con todo no sería usable.
export function MatchFormModal({
  campeonato,
  match,
  onOpenChange,
}: {
  campeonato: ChampionshipDetail;
  match: ChampionshipMatch | null;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const id = campeonato.id;

  const [fase, setFase] = useState(match?.fase ?? "");
  const [orden, setOrden] = useState(match?.orden != null ? String(match.orden) : "");
  const [court, setCourt] = useState<ChampionshipCourt | null>(match?.court ?? null);
  const [startsAt, setStartsAt] = useState(isoToLocalInput(match?.startsAt ?? null));
  const [endsAt, setEndsAt] = useState(isoToLocalInput(match?.endsAt ?? null));
  const [equipoA, setEquipoA] = useState<ChampionshipTeam | null>(match?.equipoA ?? null);
  const [equipoB, setEquipoB] = useState<ChampionshipTeam | null>(match?.equipoB ?? null);
  const [arbitro, setArbitro] = useState<Referee | null>(match?.arbitro ?? null);
  const [estado, setEstado] = useState<ChampionshipMatchStatus>(match?.estado ?? "PROGRAMADO");
  const [setsA, setSetsA] = useState(match?.setsA != null ? String(match.setsA) : "");
  const [setsB, setSetsB] = useState(match?.setsB != null ? String(match.setsB) : "");
  const [confirming, setConfirming] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const finished = !!match && match.matchEnded;
  const hasSchedule = !!(court && startsAt && endsAt);

  const fetchTeams = async (search: string) =>
    (await api.get<{ data: ChampionshipTeam[] }>(`/championships/${id}/equipos`, { params: { search, limit: 25, sort: "pareja" } })).data.data;

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        fase: fase.trim() || null,
        orden: orden !== "" ? Number(orden) : null,
        courtId: court?.id ?? null,
        startsAt: localInputToIso(startsAt),
        endsAt: localInputToIso(endsAt),
        equipoAId: equipoA?.id ?? null,
        equipoBId: equipoB?.id ?? null,
        arbitroId: arbitro?.id ?? null,
        ...(match ? { estado, setsA: setsA !== "" ? Number(setsA) : null, setsB: setsB !== "" ? Number(setsB) : null } : {}),
      };
      const url = `/championships/${id}/partidos${match ? `/${match.id}` : ""}`;
      return match ? api.put(url, body) : api.post(url, body);
    },
    onSuccess: () => {
      toast.success(match ? "Partido actualizado" : "Partido creado");
      queryClient.invalidateQueries({ queryKey: ["championship", id] });
      queryClient.invalidateQueries({ queryKey: ["championships"] });
      onOpenChange(false);
    },
    onError: (error) => toast.error(apiError(error, "No se pudo guardar el partido")),
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/championships/${id}/partidos/${match!.id}`),
    onSuccess: () => {
      toast.success("Partido eliminado");
      queryClient.invalidateQueries({ queryKey: ["championship", id] });
      queryClient.invalidateQueries({ queryKey: ["championships"] });
      onOpenChange(false);
    },
    onError: (error) => toast.error(apiError(error, "No se pudo eliminar el partido")),
  });

  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      size="lg"
      title={match ? "Editar partido" : "Nuevo partido"}
      description="Con pista y horario completos se reserva la pista y el partido entra solo en «en directo»."
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setConfirming(true);
        }}
        className="flex flex-col gap-4"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="partido-fase" className="text-sm font-medium text-ink-600">
              Fase
            </label>
            <input
              id="partido-fase"
              list="partido-fases"
              placeholder="Grupo A, Cuartos, Semifinal, Final…"
              value={fase}
              onChange={(e) => setFase(e.target.value)}
              className={selectClass}
            />
            <datalist id="partido-fases">
              {campeonato.fases.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>
          </div>
          <Input label="Orden en la fase" type="number" min={0} value={orden} onChange={(e) => setOrden(e.target.value)} className="w-full" />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SearchSelect<ChampionshipTeam>
            label="Pareja A"
            value={equipoA}
            onChange={setEquipoA}
            queryKey={["championship", id, "equipos", "picker"]}
            fetchOptions={fetchTeams}
            getKey={(t) => t.id}
            getLabel={teamLabel}
            getHint={(t) => (t.nombre ? `${personName(t.jugador1)} / ${personName(t.jugador2)}` : null)}
            placeholder="Por definir"
            searchPlaceholder="Nombre de pareja o jugador…"
            clearable
          />
          <SearchSelect<ChampionshipTeam>
            label="Pareja B"
            value={equipoB}
            onChange={setEquipoB}
            queryKey={["championship", id, "equipos", "picker"]}
            fetchOptions={fetchTeams}
            getKey={(t) => t.id}
            getLabel={teamLabel}
            getHint={(t) => (t.nombre ? `${personName(t.jugador1)} / ${personName(t.jugador2)}` : null)}
            placeholder="Por definir"
            searchPlaceholder="Nombre de pareja o jugador…"
            clearable
          />
        </div>

        <div className="flex flex-col gap-3 rounded-xl border border-ink-100 p-3">
          <p className="flex items-center gap-2 text-sm font-medium text-ink-600">
            <CalendarClock className="h-4 w-4 text-brand-600" />
            Pista y horario
          </p>
          {finished && <p className="text-xs text-amber-700">Este partido ya ha terminado: no se puede cambiar su pista ni su horario.</p>}
          <SearchSelect<ChampionshipCourt>
            value={court}
            onChange={setCourt}
            queryKey={["championship", id, "courts"]}
            fetchOptions={async (search) => (await api.get<ChampionshipCourt[]>(`/championships/${id}/courts`, { params: { search } })).data}
            getKey={(c) => c.id}
            getLabel={(c) => `${c.club.nombre} — ${c.name}`}
            placeholder="Pista por definir"
            searchPlaceholder="Club o nombre de pista…"
            disabled={finished}
            clearable
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input type="datetime-local" label="Desde" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} disabled={finished} className="w-full" />
            <Input type="datetime-local" label="Hasta" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} disabled={finished} className="w-full" />
          </div>
        </div>

        <SearchSelect<Referee>
          label="Árbitro"
          value={arbitro}
          onChange={setArbitro}
          queryKey={["championships", "referees"]}
          fetchOptions={async (search) => (await api.get<Referee[]>("/championships/referees", { params: { search } })).data}
          getKey={(r) => r.id}
          getLabel={personName}
          getHint={(r) => r.email}
          placeholder="Sin asignar"
          searchPlaceholder="Nombre o email del árbitro…"
          clearable
        />

        {match && (
          <div className="grid grid-cols-1 gap-3 rounded-xl bg-ink-50/60 p-3 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink-600">Estado</label>
              <select value={estado} onChange={(e) => setEstado(e.target.value as ChampionshipMatchStatus)} className={selectClass}>
                {(Object.keys(MATCH_STATUS_LABEL) as ChampionshipMatchStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {MATCH_STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            </div>
            <Input label="Sets pareja A" type="number" min={0} max={3} value={setsA} onChange={(e) => setSetsA(e.target.value)} className="w-full" />
            <Input label="Sets pareja B" type="number" min={0} max={3} value={setsB} onChange={(e) => setSetsB(e.target.value)} className="w-full" />
            <p className="text-xs text-ink-400 sm:col-span-3">
              El resultado se rellena solo si el partido se juega con el marcador del panel; aquí puedes anotarlo a mano si se jugó fuera.
            </p>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          {match && (
            <Button type="button" variant="danger" className="sm:mr-auto" onClick={() => setConfirmingDelete(true)}>
              <Trash2 className="h-4 w-4" />
              Eliminar partido
            </Button>
          )}
          <Button type="button" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {match ? "Guardar partido" : "Crear partido"}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title="Eliminar partido"
        description="Se eliminará el partido y, si todavía no ha empezado, se liberará la pista reservada. ¿Seguro?"
        tone="danger"
        confirmLabel="Eliminar"
        onConfirm={() => remove.mutate()}
      />

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={match ? "Guardar cambios del partido" : "Crear partido"}
        description={
          hasSchedule
            ? "Se reservará la pista en ese horario (si hay otra reserva solapada, no se guardará) y el partido entrará solo en «en directo» a su hora."
            : "El partido se guardará sin pista u horario completos, así que de momento no reservará ninguna pista."
        }
        tone="primary"
        confirmLabel={match ? "Guardar" : "Crear"}
        onConfirm={() => save.mutate()}
      />
    </Modal>
  );
}
